/**
 * Create Token Modal Component
 *
 * Form for creating new API token with name, scopes, and expiration.
 */
import React, { useState } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Button,
  Group,
  Alert,
  Text,
  Select,
  Tabs
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import {
  IconKey,
  IconAlertCircle,
  IconCalendar,
  IconInfinity
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';
import TokenScopesSelector from './TokenScopesSelector';
import { createToken } from '../../../services/api/apiTokensService';

/**
 * CreateTokenModal Component
 *
 * @param {boolean} opened - Modal open state
 * @param {Function} onClose - Close callback
 * @param {Function} onSuccess - Success callback with created token data
 */
const CreateTokenModal = ({ opened, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [expirationMode, setExpirationMode] = useState('never'); // 'never', 'preset', 'custom'
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Expiration presets
  const expirationPresets = [
    { value: '30d', label: '30 дней' },
    { value: '90d', label: '90 дней' },
    { value: '180d', label: '180 дней' },
    { value: '1y', label: '1 год' }
  ];

  const [selectedPreset, setSelectedPreset] = useState(null);

  // Calculate expiration date from preset
  const calculateExpirationFromPreset = (preset) => {
    const now = new Date();
    switch (preset) {
      case '30d':
        return new Date(now.setDate(now.getDate() + 30));
      case '90d':
        return new Date(now.setDate(now.getDate() + 90));
      case '180d':
        return new Date(now.setDate(now.getDate() + 180));
      case '1y':
        return new Date(now.setFullYear(now.getFullYear() + 1));
      default:
        return null;
    }
  };

  // Handle preset change
  const handlePresetChange = (value) => {
    setSelectedPreset(value);
    const date = calculateExpirationFromPreset(value);
    setExpiresAt(date);
  };

  // Validation
  const isFormValid = () => {
    if (!name.trim()) return false;
    if (selectedScopes.length === 0) return false;
    if (expirationMode === 'custom' && !expiresAt) return false;
    if (expirationMode === 'preset' && !selectedPreset) return false;
    return true;
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!isFormValid()) {
      setError('Заполните все обязательные поля');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Calculate final expiration date
      let finalExpiresAt = null;
      if (expirationMode === 'never') {
        finalExpiresAt = null;
      } else if (expirationMode === 'preset') {
        finalExpiresAt = calculateExpirationFromPreset(selectedPreset);
      } else if (expirationMode === 'custom') {
        finalExpiresAt = expiresAt;
      }

      const tokenData = {
        name: name.trim(),
        scopes: selectedScopes,
        expires_at: finalExpiresAt ? finalExpiresAt.toISOString() : null
      };

      const result = await createToken(tokenData);

      // Reset form
      setName('');
      setSelectedScopes([]);
      setExpirationMode('never');
      setExpiresAt(null);
      setSelectedPreset(null);

      // Call success callback with token data
      onSuccess(result);

      // Close modal
      onClose();
    } catch (err) {
      console.error('Failed to create token:', err);
      setError(err.message || 'Не удалось создать токен');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    // Reset form
    setName('');
    setSelectedScopes([]);
    setExpirationMode('never');
    setExpiresAt(null);
    setSelectedPreset(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={
        <Group gap="xs">
          <IconKey size={24} color={tokens.colors.brand.primary} />
          <Text fw={700} size="lg">
            Создать API токен
          </Text>
        </Group>
      }
      size="xl"
      centered
    >
      <Stack gap="lg">
        {/* Error alert */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Ошибка"
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Token name */}
        <TextInput
          label="Название токена"
          placeholder="Например: Parser Production"
          description="Понятное название для идентификации токена"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={loading}
          styles={{
            label: {
              fontWeight: 600,
              color: tokens.colors.neutral[8],
              marginBottom: tokens.spacing.xs
            }
          }}
        />

        {/* Scopes selector */}
        <Stack gap="xs">
          <Text size="sm" fw={600} c={tokens.colors.neutral[8]}>
            Права доступа <span style={{ color: 'red' }}>*</span>
          </Text>
          <Text size="xs" c={tokens.colors.neutral[6]}>
            Выберите, к каким API endpoints будет иметь доступ этот токен
          </Text>
          <TokenScopesSelector
            selectedScopes={selectedScopes}
            onChange={setSelectedScopes}
            disabled={loading}
          />
        </Stack>

        {/* Expiration settings */}
        <Stack gap="xs">
          <Text size="sm" fw={600} c={tokens.colors.neutral[8]}>
            Срок действия
          </Text>

          <Tabs
            value={expirationMode}
            onChange={setExpirationMode}
            variant="pills"
          >
            <Tabs.List>
              <Tabs.Tab
                value="never"
                leftSection={<IconInfinity size={16} />}
              >
                Не истекает
              </Tabs.Tab>
              <Tabs.Tab
                value="preset"
                leftSection={<IconCalendar size={16} />}
              >
                Выбрать период
              </Tabs.Tab>
              <Tabs.Tab
                value="custom"
                leftSection={<IconCalendar size={16} />}
              >
                Указать дату
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="never" pt="md">
              <Alert color="blue" variant="light">
                <Text size="sm">
                  Токен будет действовать бессрочно до ручной деактивации
                </Text>
              </Alert>
            </Tabs.Panel>

            <Tabs.Panel value="preset" pt="md">
              <Select
                placeholder="Выберите период"
                data={expirationPresets}
                value={selectedPreset}
                onChange={handlePresetChange}
                disabled={loading}
              />
              {selectedPreset && expiresAt && (
                <Text size="xs" c={tokens.colors.neutral[6]} mt="xs">
                  Токен истечёт:{' '}
                  {expiresAt.toLocaleString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Europe/Moscow'
                  })}
                </Text>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="custom" pt="md">
              <DateTimePicker
                label="Дата и время истечения"
                placeholder="Выберите дату и время"
                value={expiresAt}
                onChange={setExpiresAt}
                minDate={new Date()}
                disabled={loading}
                clearable
                valueFormat="DD.MM.YYYY HH:mm"
              />
              {expiresAt && (
                <Text size="xs" c={tokens.colors.neutral[6]} mt="xs">
                  Токен истечёт:{' '}
                  {expiresAt.toLocaleString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Europe/Moscow'
                  })}
                </Text>
              )}
            </Tabs.Panel>
          </Tabs>
        </Stack>

        {/* Action buttons */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            color="gray"
            onClick={handleCancel}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button
            variant="filled"
            color="portal-blue"
            onClick={handleSubmit}
            loading={loading}
            disabled={!isFormValid() || loading}
          >
            Создать токен
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CreateTokenModal;
